// Session storage utility for AI chat sessions
import { API_URL, fetchWithCredentials } from '../config';

const SESSIONS_KEY = 'helm_ai_sessions';

// Load sessions from backend on startup
export const syncSessionsFromBackend = async () => {
  try {
    const response = await fetchWithCredentials(`${API_URL}/api/sessions`);
    if (response.ok) {
      const data = await response.json();
      if (data.sessions) {
        localStorage.setItem(SESSIONS_KEY, JSON.stringify(data.sessions));
        console.log(`✅ Synced ${data.sessions.length} sessions from backend`);
        return data.sessions;
      }
    }
  } catch (error) {
    console.warn('Could not sync sessions from backend:', error);
  }
  return getAllSessions(); // Fallback to localStorage
};

export const getAllSessions = () => {
  try {
    const sessions = localStorage.getItem(SESSIONS_KEY);
    return sessions ? JSON.parse(sessions) : [];
  } catch (error) {
    console.error('Error loading sessions:', error);
    return [];
  }
};

export const getSession = (sessionId) => {
  const sessions = getAllSessions();
  return sessions.find(s => s.id === sessionId);
};

export const saveSession = async (sessionData) => {
  try {
    // Save to localStorage immediately
    const sessions = getAllSessions();
    const existingIndex = sessions.findIndex(s => s.id === sessionData.id);
    
    if (existingIndex >= 0) {
      sessions[existingIndex] = {
        ...sessionData,
        updatedAt: new Date().toISOString()
      };
    } else {
      sessions.unshift({
        ...sessionData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
    
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
    
    // Sync to backend
    try {
      const response = await fetchWithCredentials(`${API_URL}/api/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session: sessionData })
      });
      
      if (response.ok) {
        console.log(`✅ Session ${sessionData.id.substr(0, 8)}... saved to backend`);
      }
    } catch (backendError) {
      console.warn('Backend save failed, keeping in localStorage:', backendError);
    }
    
    return sessionData.id;
  } catch (error) {
    console.error('Error saving session:', error);
    throw error;
  }
};

export const deleteSession = async (sessionId) => {
  try {
    // Delete from localStorage
    const sessions = getAllSessions();
    const filtered = sessions.filter(s => s.id !== sessionId);
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(filtered));
    
    // Delete from backend
    try {
      const response = await fetchWithCredentials(`${API_URL}/api/sessions/${sessionId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        console.log(`✅ Session ${sessionId.substr(0, 8)}... deleted from backend`);
      }
    } catch (backendError) {
      console.warn('Backend delete failed, removed from localStorage:', backendError);
    }
  } catch (error) {
    console.error('Error deleting session:', error);
    throw error;
  }
};

export const createSession = (title, messages = [], corpusId = null) => {
  const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  // Get current corpus if not provided
  if (!corpusId) {
    corpusId = localStorage.getItem('corpus_id') || 'demo-skipper-corpus-2024';
  }
  
  return {
    id: sessionId,
    title: title || 'Untitled Session',
    messages,
    messageCount: messages.length,
    preview: messages.length > 0 ? messages[messages.length - 1].message : '',
    corpusId: corpusId,  // Store which corpus this session belongs to
    corpusName: corpusId === 'demo-skipper-corpus-2024' ? 'Skipper Demo' : 'My Data'
  };
};

